import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Event as EventIcon,
  History as HistoryIcon,
  ElectricBolt as ElectricBoltIcon,
  LocationOn as LocationOnIcon
} from '@mui/icons-material';
import { fetchParkingSlotDetails } from '../api/parkingSlots';
import SnackbarAlert from './SnackbarAlert';

function ParkingSlotDetails() {
  const { t } = useTranslation();
  const { slotId } = useParams();
  const navigate = useNavigate();
  const [slotData, setSlotData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const loadSlotDetails = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchParkingSlotDetails(slotId);
      setSlotData(data);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [slotId]);

  useEffect(() => {
    loadSlotDetails();
  }, [loadSlotDetails]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading slot details...</Typography>
      </Box>
    );
  }

  if (!slotData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Slot not found.</Typography>
      </Box>
    );
  }

  const { slot, past_reservations, future_reservations, audit_logs, slot_releases } = slotData;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/manage-parking-slots')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Parking Slot Details
        </Typography>
      </Box>

      {/* Slot Information Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <LocationOnIcon sx={{ mr: 1 }} />
            {slot.slot_number} at {slot.address}
          </Typography>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {slot.has_charger && <ElectricBoltIcon color="primary" sx={{ mr: 1 }} />}
                <Typography variant="body2" color="text.secondary">
                  Charger: {slot.has_charger ? 'Yes' : 'No'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Assignment Order: {slot.assignment_order}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Created: {formatDate(slot.created_at)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon />
                <Typography variant="body2" color="text.secondary">
                  Assigned to:
                </Typography>
                {slot.assigned_user ? (
                  <Chip
                    label={`${slot.assigned_user.name} (${slot.assigned_user.email})`}
                    color="primary"
                    size="small"
                  />
                ) : (
                  <Chip label="Unassigned" variant="outlined" size="small" />
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Accordions for different sections */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <EventIcon sx={{ mr: 1 }} />
          <Typography variant="h6">
            Future Reservations ({future_reservations.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {future_reservations.length === 0 ? (
            <Typography color="text.secondary">No future reservations.</Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Booked At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {future_reservations.map((reservation) => (
                    <TableRow key={reservation.id}>
                      <TableCell>{formatDate(reservation.date)}</TableCell>
                      <TableCell>
                        {reservation.is_guest ? (
                          `${reservation.guest_name} (${reservation.guest_company})`
                        ) : (
                          `${reservation.user_name} (${reservation.user_email})`
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={reservation.is_guest ? 'Guest' : 'User'}
                          size="small"
                          color={reservation.is_guest ? 'secondary' : 'primary'}
                        />
                      </TableCell>
                      <TableCell>
                        {reservation.timestamp ? formatDateTime(reservation.timestamp) : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <HistoryIcon sx={{ mr: 1 }} />
          <Typography variant="h6">
            Past Reservations ({past_reservations.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {past_reservations.length === 0 ? (
            <Typography color="text.secondary">No past reservations.</Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Booked At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {past_reservations.map((reservation) => (
                    <TableRow key={reservation.id}>
                      <TableCell>{formatDate(reservation.date)}</TableCell>
                      <TableCell>
                        {reservation.is_guest ? (
                          `${reservation.guest_name} (${reservation.guest_company})`
                        ) : (
                          `${reservation.user_name} (${reservation.user_email})`
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={reservation.is_guest ? 'Guest' : 'User'}
                          size="small"
                          color={reservation.is_guest ? 'secondary' : 'primary'}
                        />
                      </TableCell>
                      <TableCell>
                        {reservation.timestamp ? formatDateTime(reservation.timestamp) : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>

      {slot.assigned_user && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <PersonIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              {t('bookings.slot_releases')} ({slot_releases.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {slot_releases.length === 0 ? (
              <Typography color="text.secondary">No slot releases.</Typography>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Start Date</TableCell>
                      <TableCell>End Date</TableCell>
                      <TableCell>Created At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {slot_releases.map((release) => (
                      <TableRow key={release.id}>
                        <TableCell>{formatDate(release.start_date)}</TableCell>
                        <TableCell>{formatDate(release.end_date)}</TableCell>
                        <TableCell>{formatDateTime(release.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <HistoryIcon sx={{ mr: 1 }} />
          <Typography variant="h6">
            Audit Log ({audit_logs.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {audit_logs.length === 0 ? (
            <Typography color="text.secondary">No audit logs found.</Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {audit_logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>{formatDateTime(log.timestamp)}</TableCell>
                      <TableCell>
                        <Chip
                          label={log.action}
                          size="small"
                          color={
                            log.action === 'CREATE' ? 'success' :
                            log.action === 'DELETE' ? 'error' :
                            log.action === 'UPDATE' ? 'warning' :
                            'default'
                          }
                        />
                      </TableCell>
                      <TableCell>{log.user_name} ({log.user_email})</TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: 300 }}>
                          {log.details && Object.keys(log.details).length > 0 ? (
                            <pre style={{ fontSize: '0.75rem', margin: 0, whiteSpace: 'pre-wrap' }}>
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          ) : (
                            'No details'
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>

      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
}

export default ParkingSlotDetails;
